import type { ReactNode } from 'react';
import { formatRM, parseRM, senToInput } from '@/features/billing/money';
import type {
  RegisterField,
  RegisterRecord,
  StatusTone,
} from '@/features/registers/api';
import { klDate, klTime, toIso, todayKl } from '@/pages/calendar/time';
import { CLINIC_TZ } from '@/lib/clinic-time';

export type StaffNames = Map<string, string>;

/** "28 Aug 2026" from YYYY-MM-DD or an ISO timestamp (Kuala Lumpur). */
export function dayLabel(v: string) {
  const ymd = v.length === 10 ? v : klDate(v);
  return new Date(`${ymd}T00:00:00+08:00`).toLocaleDateString('en-GB', {
    timeZone: CLINIC_TZ,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function whenLabel(iso: string) {
  return `${dayLabel(iso)}, ${klTime(iso)}`;
}

/** Display a stored field value; never invents a value for an empty one. */
export function formatValue(
  f: RegisterField,
  v: unknown,
  staff: StaffNames,
): ReactNode {
  if (v === null || v === undefined || v === '') return '-';
  switch (f.type) {
    case 'money':
      return typeof v === 'number' ? formatRM(v) : '-';
    case 'date':
      return typeof v === 'string' ? dayLabel(v) : '-';
    case 'datetime':
      return typeof v === 'string' ? whenLabel(v) : '-';
    case 'boolean':
      return v ? 'Yes' : 'No';
    case 'select':
      return f.options?.find((o) => o.value === v)?.label ?? String(v);
    case 'multiselect':
      return Array.isArray(v)
        ? v
            .map((x) => f.options?.find((o) => o.value === x)?.label ?? x)
            .join(', ') || '-'
        : '-';
    case 'staff':
      return typeof v === 'string' ? (staff.get(v) ?? 'Staff member') : '-';
    default:
      return String(v);
  }
}

/** Form value (string/array/boolean) for a stored value. */
export function toFormValue(f: RegisterField, v: unknown): FormValue {
  if (f.type === 'multiselect') return Array.isArray(v) ? (v as string[]) : [];
  if (f.type === 'boolean') return v === true;
  if (v === null || v === undefined) return '';
  if (f.type === 'money' && typeof v === 'number') return senToInput(v);
  if (f.type === 'datetime' && typeof v === 'string')
    return `${klDate(v)}T${klTime(v)}`;
  return String(v);
}

export type FormValue = string | string[] | boolean;

/** Converts form values to the API's typed values; returns an error label if invalid. */
export function toApiData(
  fields: RegisterField[],
  values: Record<string, FormValue>,
): { data: Record<string, unknown>; invalid?: string } {
  const data: Record<string, unknown> = {};
  for (const f of fields) {
    const v = values[f.key];
    if (f.type === 'boolean') {
      data[f.key] = v === true;
      continue;
    }
    if (f.type === 'multiselect') {
      if (Array.isArray(v) && v.length) data[f.key] = v;
      else if (f.required) return { data, invalid: f.label };
      continue;
    }
    const s = typeof v === 'string' ? v.trim() : '';
    if (!s) {
      if (f.required) return { data, invalid: f.label };
      continue;
    }
    if (f.type === 'number') {
      const n = Number(s);
      if (!Number.isFinite(n)) return { data, invalid: f.label };
      data[f.key] = n;
    } else if (f.type === 'money') {
      const n = parseRM(s);
      if (n === null) return { data, invalid: f.label };
      data[f.key] = n;
    } else if (f.type === 'datetime') {
      const [d, t] = s.split('T');
      data[f.key] = toIso(d, (t ?? '00:00').slice(0, 5));
    } else data[f.key] = s;
  }
  return { data };
}

/** RL-1823 (gap #109): licences are flagged this many days before they lapse. */
export const LICENCE_SOON_DAYS = 90;

/**
 * Status shown for a register record. An open licence is "Expired" (red) once
 * its expiry has passed and "Expires soon" (amber) from 90 days before;
 * otherwise the API's status.
 */
export function shownStatus(
  r: Pick<
    RegisterRecord,
    'kind' | 'statusLabel' | 'statusTone' | 'dueAt' | 'overdue' | 'closedAt'
  >,
  today: string = todayKl(),
): { label: string; tone: StatusTone } {
  if (r.kind !== 'licence' || r.closedAt || !r.dueAt)
    return { label: r.statusLabel, tone: r.statusTone };
  const days =
    (Date.parse(`${klDate(r.dueAt)}T00:00:00Z`) -
      Date.parse(`${today}T00:00:00Z`)) /
    86_400_000;
  // Not `overdue`: the API stores the expiry day as 00:00 UTC (08:00 in
  // Malaysia), so it is overdue on the licence's last valid day.
  if (days < 0) return { label: 'Expired', tone: 'danger' };
  if (days <= LICENCE_SOON_DAYS)
    return { label: 'Expires soon', tone: 'warning' };
  return { label: r.statusLabel, tone: r.statusTone };
}
